import type { PhotoEngagement } from "@/lib/api/photoEngagement";
export interface StylistLocation {
  id: string;
  pathId: string;
  name: string;
  address: string;
  lat: number;
  lng: number;
}

/**
 * An Openings team member tag: a business-written, customer-facing label
 * (e.g. "Korean · basic English") with an optional note that the booking
 * review and confirmation show under "Before your visit".
 *
 * Shown only inside booking (Oct 21): the location page's stylist mini
 * profile, the review step and the confirmation. The Team directory, its
 * mini profile, the stylist page header and the location page's stylist
 * cards do not show them.
 */
export interface StylistTag {
  id: string;
  label: string;
  description?: string | null;
}

export interface StylistCardData {
  /** Canonical Openings identity used by the booking mini profile. */
  memberId: string;
  username: string;
  name: string;
  avatar?: string;
  instagram?: string;
  bio?: string;
  /** A successful member portfolio response, including an empty portfolio. */
  portfolioLoaded?: boolean;
  workingSince?: string | null;
  photos: (PhotoEngagement & {
    src: string;
    alt: string;
    type?: "image" | "video";
    serviceIds?: string[];
  })[];
  locationIds: string[];
  /** Bookable services at each location, so combined filters stay truthful. */
  servicesByLocation: Record<string, string[]>;
}

export interface StylistDirectory {
  stylists: StylistCardData[];
  locations: StylistLocation[];
  partial: boolean;
}

export const STYLIST_SERVICES = [
  { id: "haircuts", label: "Haircuts", categories: [1, 2, 5] },
  { id: "perms", label: "Perms", categories: [3, 10] },
  { id: "color", label: "Color", categories: [4] },
];

/** Cloudflare's small/public/large variants identify the same work photo. */
export function stylistPhotoKey(src: string): string {
  try {
    const url = new URL(src);
    if (url.hostname === "imagedelivery.net") {
      return `${url.origin}${url.pathname.replace(/\/[^/]+\/?$/, "")}`;
    }
  } catch {
    // Preserve fixture and relative URLs as-is.
  }
  return src;
}

export function filterStylistPhotos<T extends { serviceIds?: string[] }>(
  photos: T[],
  service = "",
): T[] {
  return service
    ? photos.filter((photo) => photo.serviceIds?.includes(service))
    : photos;
}

export function filterStylists(
  stylists: StylistCardData[],
  { query = "", locationId = "", service = "" } = {},
): StylistCardData[] {
  const search = query.trim().toLocaleLowerCase().replace(/^@/, "");
  return stylists.filter((stylist) => {
    if (locationId && !stylist.locationIds.includes(locationId)) return false;
    if (service) {
      const services = locationId
        ? (stylist.servicesByLocation[locationId] ?? [])
        : Object.values(stylist.servicesByLocation).flat();
      if (!services.includes(service)) return false;
    }
    return (
      !search ||
      [stylist.name, stylist.username, stylist.instagram ?? ""].some((value) =>
        value.toLocaleLowerCase().includes(search),
      )
    );
  });
}

/** Date-only tenure must keep its month regardless of the viewer's timezone. */
export function formatWorkingSince(value?: string | null): string | null {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00.000Z`);
  if (!Number.isFinite(date.getTime())) return null;
  return `Working since ${new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(date)}`;
}

export function isMappableLocation(location: StylistLocation): boolean {
  return (
    Number.isFinite(location.lat) &&
    Number.isFinite(location.lng) &&
    Math.abs(location.lat) <= 90 &&
    Math.abs(location.lng) <= 180 &&
    (location.lat !== 0 || location.lng !== 0)
  );
}
